/** Runtime facts supplied by the signed Android TWA start URL. */

const params = new URLSearchParams(location.search);

export const isAndroidApp = (): boolean =>
  params.get('app') === 'android' ||
  document.referrer.startsWith('android-app://asia.airtasker.wayfarer.gps');

/**
 * The APK is one responsive app, not a separate phone and car product. Its
 * normal URL keeps the phone layout on narrow displays while dashboard-sized
 * Android windows use the existing /car shell from the first render.
 */
export function isLargeAndroidDisplay(): boolean {
  if (!isAndroidApp()) return false;
  const width = window.visualViewport?.width ?? window.innerWidth;
  return Number.isFinite(width) && width >= 700;
}

export function androidShellCode(): number | null {
  if (!isAndroidApp()) return null;
  const parsed = Number.parseInt(params.get('shellCode') ?? '', 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

interface NativeRelease {
  available?: boolean;
  versionName?: string;
  versionCode?: number;
  apkUrl?: string;
}

/**
 * PWA code is updated by the service worker. This check is only for rare
 * native-shell changes (permissions, package metadata, Digital Asset Links).
 * Android must always ask the driver to approve a sideloaded APK update.
 */
export async function showNativeUpdateIfAvailable(parent: HTMLElement): Promise<boolean> {
  const installedCode = androidShellCode();
  if (installedCode === null) return false;

  // A foreground check from Settings may run after the startup check. Reuse
  // the existing prompt instead of stacking identical banners over the map.
  if (parent.querySelector('.shell-update')) return true;

  let release: NativeRelease;
  try {
    const res = await fetch('/download/version.json', { cache: 'no-store' });
    if (!res.ok) return false;
    release = (await res.json()) as NativeRelease;
  } catch {
    return false;
  }

  if (
    release.available !== true ||
    !Number.isSafeInteger(release.versionCode) ||
    (release.versionCode ?? 0) <= installedCode ||
    typeof release.apkUrl !== 'string' ||
    !release.apkUrl.startsWith('/download/')
  ) {
    return false;
  }

  const banner = document.createElement('aside');
  banner.className = 'shell-update';
  banner.innerHTML = `
    <span>Wayfarer ${release.versionName ?? ''} is ready.</span>
    <a class="btn btn--primary btn--small" href="${release.apkUrl}">Update app</a>
    <button class="icon-btn" type="button" aria-label="Dismiss">×</button>`;
  banner.querySelector('button')!.addEventListener('click', () => banner.remove());
  parent.appendChild(banner);
  return true;
}
